
import React from 'react';
import * as XLSX from 'xlsx';
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { fetchAllPages, TableName } from './exportUtils';

interface BulkExportProps {
  isExporting: boolean;
  setIsExporting: (value: boolean) => void;
}

export const BulkExport = ({ isExporting, setIsExporting }: BulkExportProps) => {
  const { toast } = useToast();

  const downloadAsXLSX = async () => {
    try {
      setIsExporting(true);
      
      const [
        tips,
        prizes,
        winners,
        universities,
        prizeAllocations,
        prizeAllocationResets,
        userRoles
      ] = await Promise.all([
        fetchAllPages('tips'),
        fetchAllPages('prizes'),
        fetchAllPages('winners'),
        fetchAllPages('universities'),
        fetchAllPages('prize_allocations'),
        fetchAllPages('prize_allocation_resets'),
        fetchAllPages('user_roles')
      ]);

      const wb = XLSX.utils.book_new();
      
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(tips), 'Tips');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(prizes), 'Prizes');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(winners), 'Winners');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(universities), 'Universities');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(prizeAllocations), 'Prize Allocations');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(prizeAllocationResets), 'Allocation Resets');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(userRoles), 'User Roles');

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `database_export_${timestamp}.xlsx`;

      XLSX.writeFile(wb, fileName);

      toast({
        title: "Export Complete",
        description: "Database exported successfully to Excel",
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        variant: "destructive",
        title: "Export Failed",
        description: "Failed to export database. Please try again.",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">Bulk Export</h2>
      <p className="text-muted-foreground">
        Export all tables into a single Excel file with multiple sheets.
      </p>
      <Button
        onClick={downloadAsXLSX}
        disabled={isExporting}
        className="w-full sm:w-auto"
      >
        {isExporting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Exporting...
          </>
        ) : (
          'Export All Tables (XLSX)'
        )}
      </Button>
    </div>
  );
};
